import { FC, ReactNode } from "react";

import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";

import { IdleThumbnail, IDLE_THUMBNAIL_SIZE } from "./IdleThumbnail";

import { DashboardHighlightedText } from "@/dashboard-design-system/HighlightedText";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useQuickSearchContext } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/QuickSearch";

interface ThumbnailedSummaryLayoutProps {
  title: string;
  thumbnailUrl: string | null;
  subtitle: string | null;
  subtitleTrailing?: ReactNode;
  isExpanded: boolean;
}

const FADE_TRANSITION = "opacity 200ms ease, width 250ms ease, margin-right 250ms ease";
const THUMBNAIL_SPACING = 1.5;

export const ThumbnailedSummaryLayout: FC<ThumbnailedSummaryLayoutProps> = ({
  title,
  thumbnailUrl,
  subtitle,
  subtitleTrailing,
  isExpanded,
}) => {
  const { query } = useQuickSearchContext();

  const showThumbnail = !isExpanded && Boolean(thumbnailUrl);

  return (
    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1 }}>
      <Box
        sx={(theme) => ({
          width: showThumbnail ? IDLE_THUMBNAIL_SIZE : 0,
          marginRight: showThumbnail ? theme.spacing(THUMBNAIL_SPACING) : 0,
          opacity: showThumbnail ? 1 : 0,
          flexShrink: 0,
          overflow: "hidden",
          transition: FADE_TRANSITION,
        })}
        aria-hidden={!showThumbnail}
      >
        {thumbnailUrl && <IdleThumbnail url={thumbnailUrl} />}
      </Box>
      <Stack sx={{ minWidth: 0, flex: 1 }}>
        <DashboardTypography
          variant="subtitle1"
          sx={(theme) => ({
            color: theme.palette.text.neutral.moderate,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          })}
        >
          <DashboardHighlightedText text={title} query={query} />
        </DashboardTypography>
        {(subtitle || subtitleTrailing) && (
          <Collapse in={!isExpanded} timeout="auto" unmountOnExit={false}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                overflow: "hidden",
                whiteSpace: "nowrap",
                opacity: isExpanded ? 0 : 1,
                transition: "opacity 200ms ease",
              }}
            >
              {subtitle && (
                <DashboardTypography
                  variant="body2"
                  sx={(theme) => ({
                    color: theme.palette.text.neutral.quiet,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flexShrink: subtitleTrailing ? 0 : 1,
                  })}
                >
                  {subtitle}
                </DashboardTypography>
              )}
              {subtitleTrailing}
            </Box>
          </Collapse>
        )}
      </Stack>
    </Box>
  );
};
